"use client";

import { ArrowLeft } from "lucide-react";
import Link from "next/link";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#0C1113] text-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="mb-8">
          <Link 
            href="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Portal
          </Link>
        </div>

        {/* Terms Content */}
        <div className="prose prose-invert max-w-none">
          <h1 className="text-4xl font-bold text-white mb-2">RHUNA PORTAL</h1>
          <h2 className="text-2xl font-semibold text-[#60FF8A] mb-8">Terms of Use and Privacy Policy</h2>

          <div className="space-y-8 text-gray-300 leading-relaxed">
            <section>
              <h3 className="text-xl font-semibold text-white mb-4">1. Introduction</h3>
              <p className="mb-4">
                These Terms of Use and Privacy Policy (the &ldquo;Terms&rdquo;) govern your access to and use of the Rhuna Portal (the &ldquo;Platform&rdquo;). The Platform is operated by Rhuna (&ldquo;Rhuna,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;), with infrastructure and technology support provided by Pulsar Money S.R.L., a company incorporated under the laws of the European Union (&ldquo;Pulsar Money&rdquo;).
              </p>
              <p className="mb-4">
                The Platform provides a web-based environment for community engagement and gamification, enabling users (&ldquo;Users,&rdquo; &ldquo;you,&rdquo; or &ldquo;your&rdquo;) to participate in quests, referral programs, interactive challenges, and blockchain-powered activities.
              </p>
              <p className="mb-4">
                By accessing or using the Platform, you acknowledge that you have read, understood, and agreed to be bound by these Terms. If you do not agree, you must immediately discontinue your use of the Platform. Because the Platform runs on Pulsar Money&apos;s infrastructure, the <a href="https://beta.pulsar.money/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-[#60FF8A] hover:text-[#60FF8A]/80 transition-colors">Pulsar Money Terms of Use</a> also apply in addition to these Terms of Use & Privacy Policy.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">2. Modifications</h3>
              <p className="mb-4">
                Rhuna reserves the right to amend, update, or otherwise modify these Terms at its sole discretion. Changes will take effect upon publication on the Platform unless otherwise specified. Where modifications are material, Rhuna will provide notice by displaying a message on the Platform or, if applicable, by contacting registered Users by email. Continued use of the Platform following the effective date of such changes constitutes acceptance of the revised Terms. If you do not agree to any modification, you must cease using the Platform.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">3. Services</h3>
              <p className="mb-4">
                The Platform provides access to quests, referral programs, and community campaigns designed to promote engagement and activity.
              </p>
              <p>
                Rhuna is responsible for campaign and reward design, while Pulsar Money provides the underlying infrastructure that processes and validates submissions.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">4. Accounts and Wallets</h3>
              <p>
                Some features of the Platform may require Users to connect a digital wallet. Users are solely responsible for safeguarding their wallet credentials, including private keys, passwords, and recovery phrases. Neither Rhuna nor Pulsar Money has access to or can restore lost credentials, and neither shall be liable for losses arising from the failure to secure such information.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">5. Rewards</h3>
              <p>
                Participation in certain campaigns may entitle Users to digital rewards, which may consist of, points, or other digital assets (&ldquo;Rewards&rdquo;). Rewards are discretionary, subject to availability, and may be modified, suspended, or withdrawn at Rhuna&apos;s sole discretion. Unless explicitly stated otherwise, Rewards have no monetary value, do not constitute legal tender, and shall not be treated as financial instruments or securities.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">6. Acceptable Use</h3>
              <p className="mb-4">
                Users shall use the Platform only in good faith and in accordance with these Terms. Users shall not exploit or manipulate the mechanics of quests, or Rewards, nor employ bots, scripts, or automated tools to interact with the Platform. Users shall not interfere with or disrupt the operation of the Platform or Pulsar Money&apos;s infrastructure, nor engage in any unlawful, fraudulent, or abusive conduct.
              </p>
              <p>
                Rhuna reserves the right, in its sole discretion, to suspend or terminate a User&apos;s access, invalidate Rewards, or restrict participation where such conduct is suspected or established.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">7. Intellectual Property</h3>
              <p>
                All trademarks, logos, content, and designs displayed on the Platform are the exclusive property of Rhuna or its licensors. Users may not copy, reproduce, distribute, or otherwise exploit such intellectual property without Rhuna&apos;s prior written consent.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">8. Data Collection and Processing</h3>
              <p className="mb-4">
                Rhuna collects and processes personal data in connection with the use of the Platform. Such data may include wallet addresses, on-chain submissions, technical data (such as IP addresses, browser information, and device identifiers), usage analytics, and, where provided, contact information such as email addresses.
              </p>
              <p className="mb-4">
                Data is collected and processed for the purposes of providing access to the Platform, administering quests and recording blockchain submissions, distributing Rewards, maintaining security, preventing abuse, complying with applicable laws, and communicating with Users.
              </p>
              <p>
                Due to the nature of blockchain technology, wallet addresses and submissions may be permanently visible on blockchain explorers. Rhuna cannot alter or delete such records.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">9. Data Protection Compliance</h3>
              <p className="mb-4">
                For the purposes of applicable data protection and privacy laws, including the UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data (the &ldquo;UAE PDPL&rdquo;), and where applicable, the General Data Protection Regulation (EU) 2016/679 (&ldquo;GDPR&rdquo;), Spicenet, Inc. acts as the Data Controller with respect to personal data collected through the Platform. Pulsar Money S.R.L. acts as a Data Processor, providing infrastructure and technical services necessary for the proper functioning and security of the Platform.
              </p>
              <p className="mb-4">
                Personal data processed may include wallet addresses, on-chain submissions, technical identifiers (such as IP addresses and device information), analytics, and, where voluntarily provided, contact details such as email addresses.
              </p>
              <p className="mb-4">
                Where data is transferred internationally — including transfers between the United Arab Emirates, the European Union, and the United States — Spicenet ensures that appropriate safeguards are implemented, in compliance with the UAE PDPL and GDPR where applicable. These safeguards may include the execution of Standard Contractual Clauses, adequacy mechanisms, or other legally recognized transfer tools to ensure equivalent protection of personal data.
              </p>
              <p className="mb-4">
                Both Rhuna and Pulsar Money implement appropriate technical and organizational measures to protect personal data against unauthorized access, disclosure, alteration, or destruction. In the event of a data breach, Spicenet will notify affected Users and relevant supervisory authorities in accordance with the notification obligations set out under the UAE PDPL and other applicable laws.
              </p>
              <p className="mb-4">
                Personal data shall be retained only for as long as necessary to fulfill the purposes for which it was collected, to comply with legal obligations, or until a User exercises their right to deletion. Blockchain submissions are immutable by design and cannot be deleted or altered once recorded on-chain.
              </p>
              <p>
                Users located in jurisdictions governed by data protection regulations, including the UAE, the European Union, and the United States, may have rights to access, rectify, erase, restrict, or object to the processing of their personal data, and to request data portability, subject to applicable legal limits.
              </p>
              <p>
                Requests to exercise these rights or inquiries regarding data protection matters may be submitted to: <a href="mailto:info@rhuna.com" className="text-[#60FF8A] hover:text-[#60FF8A]/80 transition-colors">info@rhuna.com</a>.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">10. Data Sharing and International Transfers</h3>
              <p className="mb-4">
                Personal data may be shared with Pulsar Money as the infrastructure provider, with service providers engaged by Rhuna to support the Platform, or with regulators and public authorities where required by law.
              </p>
              <p>
                Where personal data is transferred outside the European Union, Rhuna ensures that appropriate safeguards are applied, including the use of Standard Contractual Clauses or equivalent mechanisms consistent with GDPR requirements.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">11. Retention of Data</h3>
              <p>
                Personal data collected in connection with the Platform will be retained only for as long as necessary to fulfill the purposes for which it was collected, to meet legal obligations, or until a User requests deletion, subject to applicable law. Blockchain submissions are immutable and cannot be altered or erased once recorded.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">12. Disclaimers</h3>
              <p className="mb-4">
                The Platform is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. Rhuna makes no representations or warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
              </p>
              <p>
                Participation in blockchain activities involves inherent risks, including technical failures, cybersecurity vulnerabilities, and regulatory changes. Users acknowledge and accept these risks by using the Platform.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">13. Limitation of Liability</h3>
              <p>
                To the fullest extent permitted by law, Rhuna shall not be liable for any indirect, incidental, consequential, or punitive damages, including but not limited to loss of funds, data, goodwill, or opportunities, arising from the use of the Platform. Rhuna&apos;s total liability to any User for claims arising under these Terms shall not exceed the amount, if any, paid by the User to Rhuna in connection with access to the Platform.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">14. Suspension and Termination</h3>
              <p>
                Rhuna may suspend, restrict, or terminate a User&apos;s access to the Platform at any time and without notice where the User breaches these Terms, engages in fraudulent or abusive conduct, or where suspension is necessary to protect the integrity or security of the Platform.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">15. Governing Law and Jurisdiction</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates, as applied in the Emirate of Dubai. Any dispute, claim, or controversy arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the competent courts of Dubai, United Arab Emirates, to the exclusion of any other jurisdiction.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-white mb-4">16. Contact Information</h3>
              <p className="mb-4">
                For any questions or requests relating to these Terms, including privacy matters, please contact:
              </p>
              <div className="bg-gray-800/50 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-white mb-4">Rhuna Support</h4>
                <div className="space-y-2">
                  <p className="flex items-center gap-2">
                    <span>📧</span>
                    <a href="mailto:info@rhuna.com" className="text-[#60FF8A] hover:text-[#60FF8A]/80 transition-colors">
                      info@rhuna.com
                    </a>
                  </p>
                  <p className="flex items-center gap-2">
                    <span>🌐</span>
                    <a href="https://rhuna.io/en" target="_blank" rel="noopener noreferrer" className="text-[#60FF8A] hover:text-[#60FF8A]/80 transition-colors">
                      https://rhuna.io/en
                    </a>
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
